"use client";

import React, { useEffect, useRef, useState } from "react";
import { Factory, Scissors, Droplets, SearchCheck, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const timelineSteps = [{
  id: "asset-sourcing",
  title: "Asset Sourcing",
  description: "Identifying high-yield commercial opportunities.",
  icon: SearchCheck,
  process: "Our acquisitions team utilizes proprietary data analytics to identify undervalued commercial properties in emerging markets. We focus on assets with strong structural integrity and significant value-add potential.",
  equipment: "Proprietary valuation models, GIS mapping software, and on-site structural engineering assessments.",
  standards: "Strict 12-point due diligence, RICS valuation standards, and ESG environmental impact assessments."
}, {
  id: "lease-optimization",
  title: "Institutional Leasing",
  description: "Securing long-term Grade-A corporate tenants.",
  icon: Scissors,
  process: "We transform acquired assets into premium workspaces or logistics hubs, attracting institutional-grade tenants. Our lease structures are designed for inflation-linked growth and maximum occupancy stability.",
  equipment: "Premium fit-out contractors, smart building IoT systems, and automated property management platforms.",
  standards: "BREEAM certification targets, ISO 14001 compliance, and transparent tenant vetting protocols."
}, {
  id: "income-generation",
  title: "Yield Management",
  description: "Generating consistent monthly capital distributions.",
  icon: Droplets,
  process: "Revenues from institutional leases are collected and distributed to investors. Our active management ensures operational costs are minimized while maintaining the highest service standards for tenants.",
  equipment: "Automated distribution engines, real-time investor dashboards, and secure financial clearing systems.",
  standards: "IFRS financial reporting, quarterly independent audits, and FCA-regulated compliance frameworks."
}, {
  id: "stability-exit",
  title: "Stability & Exit",
  description: "Strategic asset appreciation and capital realization.",
  icon: Factory,
  process: "Through continuous asset enhancement and market timing, we optimize the portfolio for capital growth. Strategic exits are executed when market conditions maximize the internal rate of return for partners.",
  equipment: "Market liquidity analysis tools, secondary market trading platforms, and capital gains tax optimization models.",
  standards: "AQL 1.5 performance benchmarks, full capital preservation focus, and transparent exit reporting."
}];
function TimelineItem({
  step,
  index
}: {
  step: typeof timelineSteps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5,
    once: false
  });
  const isEven = index % 2 === 0;
  const Icon = step.icon;
  return <div data-section-id="93134" ref={ref} className={`relative flex flex-col md:flex-row items-start md:items-center mb-24 last:mb-0 ${isEven ? 'md:flex-row-reverse' : ''}`} data-index={index}>
      {}
      <div className={`absolute left-4 md:left-1/2 w-12 h-12 rounded-full border-4 -translate-x-1/2 flex items-center justify-center transition-all duration-700 z-10 shadow-lg ${isVisible ? 'bg-background border-ring text-ring shadow-ring/30 scale-110' : 'bg-card border-muted text-muted-foreground scale-100'}`}>
        <Icon className={`w-5 h-5 transition-transform duration-700 delay-100 ${isVisible ? 'rotate-0 scale-100' : '-rotate-90 scale-50 opacity-0'}`} />
      </div>

      {}
      <div className={`ml-16 md:ml-0 w-[calc(100%-4rem)] md:w-[calc(50%-4rem)] ${isEven ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'}`}>
        <Card className={`bg-card/90 backdrop-blur-xl border-border/60 shadow-xl hover:scale-[1.02] transition-all duration-700 ease-out group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <CardHeader className="pb-4 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-2 relative z-10">
              <span className="text-xs font-mono font-bold tracking-widest text-ring uppercase">Phase 0{index + 1}</span>
              {isVisible && <CheckCircle2 className="w-4 h-4 text-ring animate-in fade-in zoom-in duration-500" />}
            </div>
            <CardTitle className="text-2xl font-default tracking-tight relative z-10">{step.title}</CardTitle>
            <CardDescription className="text-base text-muted-foreground relative z-10">{step.description}</CardDescription>
            <span className="absolute -top-4 -right-2 text-8xl font-serif font-bold opacity-[0.03] select-none pointer-events-none group-hover:opacity-[0.05] transition-opacity duration-500">
              {index + 1}
            </span>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="process" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6 bg-muted/30 p-1 rounded-md">
                <TabsTrigger value="process" className="text-xs sm:text-sm rounded-sm font-default">Strategy</TabsTrigger>
                <TabsTrigger value="equipment" className="text-xs sm:text-sm rounded-sm font-default">Systems</TabsTrigger>
                <TabsTrigger value="standards" className="text-xs sm:text-sm rounded-sm font-default">Governance</TabsTrigger>
              </TabsList>
              <div className="min-h-[100px]">
                <TabsContent value="process" className="text-sm text-foreground/70 leading-relaxed mt-0 animate-in fade-in slide-in-from-bottom-2 duration-500 font-default">
                  {step.process}
                </TabsContent>
                <TabsContent value="equipment" className="text-sm text-foreground/70 leading-relaxed mt-0 animate-in fade-in slide-in-from-bottom-2 duration-500 font-default">
                  {step.equipment}
                </TabsContent>
                <TabsContent value="standards" className="text-sm text-foreground/70 leading-relaxed mt-0 animate-in fade-in slide-in-from-bottom-2 duration-500 font-default">
                  {step.standards}
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function QualityAssuranceTimeline() {
  const [progress, setProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const viewportCenter = windowHeight / 2;
      const containerTop = rect.top;
      const containerHeight = rect.height;
      const scrolled = viewportCenter - containerTop;
      let newProgress = scrolled / containerHeight * 100;
      newProgress = Math.max(0, Math.min(100, newProgress));
      setProgress(newProgress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="how-it-works" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-[-10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-1/4 right-[-10%] w-[40%] h-[40%] rounded-full bg-ring/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <h2 className="text-sm font-mono font-bold tracking-[0.3em] text-ring uppercase mb-4">Heritage Gold Strategy</h2>
          <h3 className="text-4xl md:text-5xl font-serif font-medium mb-6 tracking-tight text-foreground">The Anatomy of Growth</h3>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            Experience a meticulous investment journey designed for wealth preservation. From strategic sourcing to capital realization, our institutional-grade process ensures stability at every stage.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto" ref={containerRef}>
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-1 bg-muted/40 rounded-full md:-translate-x-1/2" />
          
          {}
          <div className="absolute left-4 md:left-1/2 top-0 w-1 bg-ring rounded-full md:-translate-x-1/2 transition-all duration-300 ease-out shadow-[0_0_20px_rgba(var(--ring),0.6)]" style={{
          height: `${progress}%`
        }} />

          <div className="py-12">
            {timelineSteps.map((step, index) => <TimelineItem key={step.id} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}